<template>
  <div
    :id="props.id"
    :class="{ isopen: props.isopen, printview: props.isprintView, 'booking-pill': !props.isproject && !props.derived, derived: props.derived }"
    :title="props.derived ? `Kommt aus ${props.derivedFrom} – anklicken führt dorthin` : null"
    :style="taskStyle"
    class="task"
    ref="taskEl"
    @click="handleClick"
    @dblclick="handleDoubleClick"
    @touchend="handleTouchEnd"
  >

<div v-if="props.isproject" :class="props.isprintView ? 'day-container-pdf' : 'day-container'">
  <div v-for="d in dayCells" :key="d.index"
       :class="props.isprintView ? 'day-cell-pdf' : 'day-cell' + (props.project.id === 0 ? ' available' : '')"
       :style="{ width: widthPerDay + 'px', minWidth: widthPerDay + 'px', color: dayCellColor() }">
    {{ d.label > 0 ? d.label : "" }}
  </div>
</div>

<!-- Abgeleitete Buchung (Entwurf Bjoern, TIM-74): keine Flaeche, sondern eine
     Linie mit Anker und dem Namen ihrer Quelle darueber. Eine gefuellte Pille
     heisst "eigene Buchung", eine Linie heisst "kommt woanders her" - das trennt
     staerker als Helligkeit, die schnell wie "vergangen" oder "inaktiv" wirkt. -->
<template v-if="props.derived && !props.isprintView">
      <div class="derived-anchor" :style="{ backgroundColor: derivedColor }"></div>
      <div class="derived-body">
        <div class="derived-source" :style="{ color: derivedColor }">{{ props.derivedFrom }}</div>
        <div class="derived-main">
          <span class="derived-name">{{ personName }}</span>
          <span class="derived-line" :style="{ backgroundColor: derivedColor }"></span>
        </div>
      </div>
    </template>

    <template v-else-if="!props.isproject && ((props.isprintView && (booking.project_id === props.project.id)) || !props.isprintView)">

      <div v-if="weekStartCells.length === 0"
       class="content" :style="{marginLeft: '12px', color: 'gray'}">
      {{ props.isprintView ? "" : props.label}}
      </div>
      <div
        v-for="d in weekStartCells"
        class="day-cell"
        :style="{ position: 'absolute', left: d.index * widthPerDay + 'px', marginLeft: '12px', color: 'gray' }"
      >
      {{ props.isprintView ? "" : props.label}}
      </div>
    </template>

    <div v-if="!props.locked && !props.derived" class="resize-handle left" @pointerdown="handleResize"></div>
    <div v-if="!props.locked && !props.derived" class="resize-handle right" @pointerdown="handleResize"></div>
    <div v-if="!props.locked && !props.derived" class="drag-handle" @pointerdown="handleDrag"></div>
  </div>

</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, inject, nextTick } from 'vue';
import { POPUP_BUS } from './popupBus';
import * as d3 from 'd3';

import {transparent, readableProjectColor} from '@/utils/globals'
import {useViewStore} from "@/stores/viewStore"
import {getWidthPerDay, rowHeight, dayMs, snap, validateProjectDuration, countBookingsForDay, getBookings, cleanx, handleAutoScroll, isWeekstart} from "./planner.js";
import {fromIso, toIso, addDays} from '@/utils/dateUtils';

const props = defineProps({
  id: { type: String, required: true},
  isproject: { type: Boolean, required: true},
  project: { type: Object, required: true},
  isprintView: {type: Boolean, required: true},
  user:  { type: Object},
  booking: { type: Object},
  bookings: { type: Array},
  label: { type: String},
  // Entitäts-Zeitraum als ISO "yyyy-mm-dd" (Modell-Typ), NICHT als Date.
  start: { type: String, required: true},
  end: { type: String, required: true},
  // Zeitachsen-Grenzen bleiben Date (D3-Domain).
  startDate: { type: Date, required: false},
  endDate: { type: Date, required: false},
  inproject: { type: Boolean, required: true},
  configs: { type: Array, required: true },
  isopen: { type: Boolean },
  // Abgeleitete Buchung (TIM-73): sie gehoert ihrer Quelle im Unterprojekt und
  // ist deshalb nicht von Hand aenderbar.
  derived: { type: Boolean, default: false },
  // Name des Bereichs, aus dem eine abgeleitete Buchung stammt.
  derivedFrom: { type: String, default: '' },
  // Die Personen, die im Projekt eine Zeile bekommen. Der Tageszaehler im
  // Kopfbalken zaehlt nur diese, sonst steht die Zahl ueber einer leeren
  // Flaeche (TIM-71). Ohne die Angabe bleibt es beim alten Verhalten.
  visibleUserIds: { type: Object, default: null },
  // Gesperrte Balken lassen sich nicht ziehen oder in der Groesse aendern.
  // Gesetzt bei Projekten mit mehreren Abschnitten (TIM-38): dort waere nicht
  // eindeutig, was ein Zug am Balken mit den uebrigen Abschnitten machen soll -
  // geaendert wird ueber den Zeitraum-Dialog.
  locked: { type: Boolean, default: false },
});

const emit = defineEmits(['bookingDone', 'taskChange', 'cancel', 'doubleClick']);

const view = useViewStore();

const widthPerDay = computed(() => {
  return getWidthPerDay(props.isprintView, view.zoomWidthPerDay)
})

const activeStartDate = computed(() => {
  if (props.startDate) return props.startDate;
  const config = props.configs.find(c => c.name === "startdate");
  return fromIso(config.value);
});

const activeEndDate = computed(() => {
  if (props.endDate) return props.endDate;
  const config = props.configs.find(c => c.name === "enddate");
  return fromIso(config.value);
});

// 3. Make the D3 Scale reactive
// By using a computed property for 'x', every style calculation 
// depending on x() will automatically update when the dates shift.
const x = computed(() => {
  const totalDays = (activeEndDate.value - activeStartDate.value) / dayMs;
  //console.log("totalDays=" + totalDays)
  //console.log("activeStartDate=" + activeStartDate.value)
  //console.log("activeEndDate=" + activeEndDate.value)
    return d3.scaleUtc()
    .domain([activeStartDate.value, activeEndDate.value])
    .range([0, totalDays * widthPerDay.value]);
});

// Local state
const currentStart = ref(props.start);
const currentEnd   = ref(props.end);

watch(() => props.start, newStart => currentStart.value = newStart);
watch(() => props.end,   newEnd   => currentEnd.value   = newEnd);

function getUserBookings() {
	if (!props.bookings || props.bookings.length === 0) {
		return []
	}	
	return props.bookings.filter(b => b.user_id === props.user.id)	
}

const pBookings = computed(() => getBookings(props.project, props.bookings))
const uBookings = computed(() => props.user ? getUserBookings() : [])

const dayCells = computed(() => {
	  const cells = [];

		let d = currentStart.value;
		let i = 0;

		while (d <= currentEnd.value) {
		  cells.push({
		    index: i,
        day: d,
		    label: countBookingsForDay(view, props.project, pBookings.value, d, props.visibleUserIds),
		  });
		  d = addDays(d, 1);
		  i++;
		}
	  return cells;
});

const weekStartCells = computed(() => {
	  const cells = [];

		let d = currentStart.value;
		let i = 0;

		while (d <= currentEnd.value) {
      if (isWeekstart(fromIso(d))) {
		    cells.push({
		      index: i,
          day: d,
		    });
      }
      d = addDays(d, 1);
		  i++;
		}
	  return cells;
});

function projectColor(props) {
  // Aufgeklapptes Projekt (TIM-61): der Kopfbalken bleibt farblos, damit die
  // Auslastung in den Personenzeilen darunter nicht gegen eine volle Flaeche
  // konkurriert. Die Zugehoerigkeit zeigt stattdessen die Outline um das
  // gesamte Projekt in ProjectTimesheet.vue.
  if (props.isproject && props.isopen && !props.isprintView) return 'transparent'

  // Ein Bereich traegt seine Zugehoerigkeit im Rahmen, nicht in der Flaeche -
  // sonst sieht ein zugeklappter Bereich aus wie ein zugeklapptes Projekt und
  // zieht mehr Aufmerksamkeit auf sich als das Projekt darueber (TIM-73).
  if (props.isproject && props.project.parent_id && !props.isprintView) return 'transparent'

  const pcolor = props.isprintView ? 'black' : props.project.color;
  return props.inproject ? transparent(pcolor, 0.5) : 'transparent'
}

// Der farblose Kopfbalken (TIM-61) traegt seine Erkennbarkeit ueber die
// Tageszahlen: die stehen dann in der Projektfarbe, passend zur Outline -
// abgedunkelt, weil die rohe Pastellfarbe als 12-px-Text nicht lesbar ist.
// Inline, weil .day-cell die Farbe in der Klasse festsetzt. Die Zeile
// "verfuegbar" (Projekt 0) behaelt ihre eigene helle Schrift.
function dayCellColor() {
  if (!props.isproject || props.isprintView) return undefined
  if (props.project.id === 0) return undefined
  // Farblos ist der Balken beim aufgeklappten Projekt und bei jedem Bereich -
  // in beiden Faellen tragen die Zahlen die Farbe.
  if (!props.isopen && !props.project.parent_id) return undefined
  return readableProjectColor(props.project.color)
}

function projectBorderColor(props) {
  if (props.isprintView) {
    return '1px solid black'
  }
  return props.inproject ? props.project.color : '1px solid gray'
}

// Farbe fuer Anker, Herkunftsname und Linie: dieselbe abgedunkelte
// Projektfarbe wie bei den Tageszahlen - 9-px-Text in roher Pastellfarbe waere
// auf hellem Grund nicht lesbar.
const derivedColor = computed(() => readableProjectColor(props.project.color))

// Breite des Balkens in Pixeln - dieselbe Rechnung wie in taskStyle, nur
// getrennt, weil die Beschriftung davon abhaengt.
const barWidthPx = computed(() => {
  const links  = cleanx(x.value, fromIso(currentStart.value)) - (props.isproject ? 5 : 0)
  const rechts = cleanx(x.value, fromIso(currentEnd.value))
  return rechts - links + widthPerDay.value - 5
})

// "Rado Ra", nicht "Rado Hric": dieselbe Form wie in der Seitenleiste. Der
// Nachname taucht in der Oberflaeche sonst nirgends auf, und zwei Schreibweisen
// fuer dieselbe Person nebeneinander lesen sich wie zwei Personen.
//
// Entweder ganz oder nur das Kuerzel - nie beides, nie ein halber Name. Grob
// gerechnet mit 6 px je Zeichen bei 11 px Schrift, dazu Anker und Rand; im
// Zweifel lieber eine Stelle zu frueh auf das Kuerzel wechseln.
const personName = computed(() => {
  const u = props.user
  if (!u) return props.label
  const lang = [u.firstname, u.short].filter(Boolean).join(' ')
  if (!lang) return u.short ?? ''
  return barWidthPx.value >= lang.length * 6 + 26 ? lang : u.short
})

const taskStyle = computed(() => {
  // Use .value because x is now a computed ref
  const topPx = props.isprintView ? 5 : 3;
  const leftPx = cleanx(x.value, fromIso(currentStart.value)) - (props.isproject ? 5 : 0);
  const widthPx = barWidthPx.value;
  const heightPx = props.isprintView ? 10 : (rowHeight - 12);

  // Abgeleitet: weder Flaeche noch Umrandung, die Zeichnung steckt im Inhalt.
  // Etwas hoeher als eine Pille, weil Herkunft und Name zwei Zeilen brauchen.
  if (props.derived && !props.isprintView) {
    return {
      top: '2px',
      left: `${leftPx}px`,
      width: `${widthPx}px`,
      height: `${rowHeight - 6}px`,
      color: 'black',
      backgroundColor: 'transparent',
      border: 'none',
    };
  }

  return {
    top: `${topPx}px`,
    left: `${leftPx}px`,
    width: `${widthPx}px`,
    height: `${heightPx}px`,
    color: 'black',
    backgroundColor: projectColor(props),
    border: projectBorderColor(props)
  };
});

const getId = () => props.isproject ? props.project.id : props.booking.id;

function updateBooking(cstart, cend, shift, changedir, newpstart, newpend) {
  const params = {
    isproject: props.isproject,
    id: getId(),
    booking_action: props.project.id,
    selected_pid: props.project.id, 
    selected_bid: props.booking ? props.booking.id : null,
    firstDay: cstart,
    lastDay: cend,
    newpstart: newpstart,
    newpend: newpend,
    selectedResources: props.user ? [props.user.id] : [],
    shift: shift,
    changedir: changedir,
  };
  emit('bookingDone', params);

  emit('taskChange', params);
}

async function updateTask(taskDiv, initialWidth, initialLeft, cstart, cend, shift, changedir) {

  // Zentrale Konfliktprüfung (Fremdbuchung derselben Person) über den Bus –
  // liefert die anzuwendende Spanne (ggf. gekappt) oder null (abbrechen).
  //
  // Nur für Buchungen: Ein Projektbalken gehört zu keiner Person und zu keiner
  // Buchung, props.user und props.booking sind dort nicht gesetzt. Der Zugriff
  // darauf endete beim Ziehen eines Projektbalkens im TypeError.
  const resolved = (!props.isproject && bus?.resolveConflict)
    ? await bus.resolveConflict({
        userId: props.user.id, excludeId: props.booking.id,
        startIso: cstart, endIso: cend,
        origStartIso: props.booking.start, origEndIso: props.booking.end,
        projectId: props.project.id,
      })
    : [cstart, cend];
  if (!resolved) {
    setTaskDim(taskDiv, initialWidth, initialLeft);
    return;
  }
  [cstart, cend] = resolved;

  let [validate, extend, newpstart, newpend] = validateProjectDuration(props.project, cstart, cend);

  if (!validate) {
    setTaskDim(taskDiv, initialWidth, initialLeft);
    return;
  }

  // Ein Leistungsbereich wird ins Hauptprojekt gespiegelt: liegt die Buchung in
  // einer Pause von dessen Laufzeit, muss zuerst diese angepasst werden - sonst
  // laege der Spiegel mitten in der Pause (TIM-73).
  if (!props.isproject && bus?.ensureParentCovers) {
    const weiter = await bus.ensureParentCovers(props.project, cstart, cend);
    if (!weiter) {
      setTaskDim(taskDiv, initialWidth, initialLeft);
      return;
    }
  }

  currentStart.value = cstart;
  currentEnd.value = cend;

  updateBooking(cstart, cend, shift, changedir, extend ? newpstart : null, extend ? newpend : null);
}

function getTask(e) {
  return e.target.closest('.task');
}

const bus = inject(POPUP_BUS);
let clickTimeout = null;

// Double-tap detection for iPad/touch devices
let lastTapTime = 0;
let lastTapTarget: EventTarget | null = null;

function handleTouchEnd(e: TouchEvent) {
  if (props.isprintView) return;
  const now = Date.now();
  const isSameTarget = lastTapTarget === e.currentTarget;
  if (isSameTarget && now - lastTapTime < 300) {
    e.preventDefault();
    lastTapTime = 0;
    handleDoubleClick(e as unknown as MouseEvent);
  } else {
    lastTapTime = now;
    lastTapTarget = e.currentTarget;
  }
}

// Ein Klick auf eine abgeleitete Buchung fuehrt zu ihrer Quelle: der Bereich
// klappt auf, und die Buchung dort wird hervorgehoben (Wunsch Bjoern). Das
// beantwortet "woher kommt die?" nicht mit Text, sondern indem es hinfuehrt -
// aendern laesst sie sich ohnehin nur dort.
async function zurQuelleSpringen() {
  const quellId = props.booking?.derived_from_id
  const quelle = (props.bookings ?? []).find(x => Number(x.id) === Number(quellId))
  if (!quelle) return

  if (!view.isOpenProject(quelle.project_id)) view.toggleProject(quelle.project_id)
  // Zweimal warten: der erste Durchlauf baut die Zeilen des Bereichs auf, erst
  // danach gibt es das Element, das hervorgehoben werden soll.
  await nextTick()
  await nextTick()

  document.querySelectorAll(".highlight-bar").forEach(t => t.classList.remove("highlight-bar"))
  const el = document.getElementById(`${quelle.project_id}-${quelle.user_id}-${quelle.id}`)
  if (!el) return
  el.classList.add("highlight-bar")
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
}

function handleClick(e) {
  bus.closePopup();

  if (clickTimeout) clearTimeout(clickTimeout);

  if (props.isprintView) {
    return
  }

  if (props.derived) {
    zurQuelleSpringen();
    return;
  }

  clickTimeout = setTimeout(() => {
    clickTimeout = null;

    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel) sel.removeAllRanges();
    }

    document.querySelectorAll(".task.editable").forEach(t => t.classList.remove("editable"));
    const taskDiv = getTask(e);
    taskDiv.classList.add("editable");
    let content = taskDiv.querySelector('.content');
    if (content) {
      content.style.display = "none";
    }
    
  }, 200);
}

function handleDoubleClick(e) {
  if (clickTimeout) {
    clearTimeout(clickTimeout);
    clickTimeout = null;
  }

  // Eine abgeleitete Buchung wird in ihrer Quelle geaendert, nicht hier.
  if (props.derived) return;

  if (props.isprintView) {
    return
  }

  document.querySelectorAll(".editable").forEach(t => t.classList.remove("editable"));
  document.querySelectorAll(".highlight-bar").forEach(t => t.classList.remove("highlight-bar"));

  const taskDiv = getTask(e);
  taskDiv.classList.add("highlight-bar");

  bus.openPopupAt(e.currentTarget, {
    id: getId(),
    html_id: props.id,
    uid: props.isproject ? null : props.user.id,
    start: currentStart.value,
    end: currentEnd.value,
    isproject: props.isproject
  });
}

function setTaskDim(taskDiv, width, left) {
  taskDiv.style.left = `${left}px`;
  taskDiv.style.width = `${width}px`;	
}

function handleDrag(e) {
  e.preventDefault();
  e.stopPropagation();

  window.__POINTER_DOWN__ = true;

  let dragHandle = e.target;
  let taskDiv = getTask(e);

  const container = document.querySelector('.main-scroll-area');

  const startScrollLeft = container.scrollLeft;
  const resizeStartX = e.clientX;
  const initialLeft = parseFloat(taskDiv.style.left);
  const initialWidth = parseFloat(taskDiv.style.width);

  let cstart = null;
  let cend   = null;

  dragHandle.setPointerCapture(e.pointerId);

  let dx = 0;
  const onPointerMove = e2 => {
    handleAutoScroll(e2);

    const scrollDx = container.scrollLeft - startScrollLeft;
    dx = snap((e2.clientX - resizeStartX) + scrollDx, view.zoomWidthPerDay);

    let newLeft = snap(initialLeft + dx, view.zoomWidthPerDay);
    newLeft = Math.max(0, newLeft);
    taskDiv.style.left = `${newLeft}px`;

    // Abgetastet wird in der Zellmitte, nicht an der Kante: dort ist das
    // Ergebnis unempfindlich gegen Rundung und gegen den 5-px-Versatz, mit dem
    // die Balken sitzen. Die frühere Verschiebung um eine ganze Zellbreite war
    // die Kompensation der lokalen Achse und ist mit TIM-36 entfallen.
    const halbeZelle = widthPerDay.value / 2;
    cstart = toIso(x.value.invert(newLeft + halbeZelle));
    cend   = toIso(x.value.invert(newLeft + initialWidth - halbeZelle));

    emit('taskChange', cstart, cend);
  };

  const onPointerUp = () => {
    window.__POINTER_DOWN__ = false;
    updateTask(taskDiv, initialWidth, initialLeft, cstart, cend, true, dx > 0);
    dragHandle.removeEventListener("pointermove", onPointerMove);
    dragHandle.removeEventListener("pointerup", onPointerUp);
  };

  dragHandle.addEventListener("pointermove", onPointerMove);
  dragHandle.addEventListener("pointerup", onPointerUp);
}

function handleResize(e) {
  e.preventDefault();
  e.stopPropagation();

  window.__POINTER_DOWN__ = true;


  const taskDiv = getTask(e);
  const initialWidth = Math.round(parseFloat(taskDiv.style.width));
  const initialLeft  = Math.round(parseFloat(taskDiv.style.left));
  const isLeft = e.target.closest('.resize-handle').classList.contains('left');

  const container = document.querySelector('.main-scroll-area');
  const startScrollLeft = container.scrollLeft;
  const resizeStartX = e.clientX;

  taskDiv.setPointerCapture(e.pointerId);
  let newLeft  = null;
  let newWidth = null;
  let cstart = null;
  let cend   = null;

  let dx = 0;
  const onPointerMove = (e2) => {
    handleAutoScroll(e2);
    const scrollDx = container.scrollLeft - startScrollLeft;
    dx = snap((e2.clientX - resizeStartX) + scrollDx, view.zoomWidthPerDay);
    if (dx === 0) return;

    if (isLeft) {
      newLeft = snap(initialLeft + dx, view.zoomWidthPerDay);
      newWidth = snap(initialWidth - dx, view.zoomWidthPerDay);
    } else {
      newLeft = snap(initialLeft, view.zoomWidthPerDay);
      newWidth = snap(initialWidth + dx, view.zoomWidthPerDay);
    }

    // Ein Balken kann nicht kuerzer als einen Tag werden: zieht man den Griff
    // weiter, bleibt er stehen, statt unsichtbar ins Negative zu laufen. Vorher
    // wurde ab 20 Pixeln nur nicht mehr gezeichnet - die Spanne unten wurde
    // trotzdem weitergerechnet, newLeft wanderte nach rechts und newWidth ins
    // Negative, und cend landete vor cstart. Siehe TIM-55.
    //
    // Die Mindestbreite ist bewusst widthPerDay und wird nicht nachgesnappt:
    // snap() rastert hier auf eine andere Breite als eine Tageszelle wirklich
    // hat (Aufruf mit zwei statt drei Argumenten, s. TIM-55). Der Anschlag soll
    // davon unabhaengig sein.
    const minWidth = widthPerDay.value
    if (newWidth < minWidth) {
      if (isLeft) newLeft = (initialLeft + initialWidth) - minWidth
      newWidth = minWidth
    }

    setTaskDim(taskDiv, newWidth, newLeft)

    // s. handleDrag: Abtastung in der Zellmitte.
    const halbeZelle = widthPerDay.value / 2;
    cstart = toIso(x.value.invert(newLeft + halbeZelle));
    cend   = toIso(x.value.invert(newLeft + newWidth - halbeZelle));
    emit('taskChange', cstart, cend);
  };

  const onPointerUp = () => {
    window.__POINTER_DOWN__ = false;

    if (dx === 0) return;

    updateTask(taskDiv, initialWidth, initialLeft, cstart, cend, false, (isLeft && dx > 0) || (!isLeft && dx < 0));

    taskDiv.removeEventListener("pointermove", onPointerMove);
    taskDiv.removeEventListener("pointerup", onPointerUp);
  };

  taskDiv.addEventListener("pointermove", onPointerMove);
  taskDiv.addEventListener("pointerup", onPointerUp);
}

onMounted(() => {
  const taskDiv = document.getElementById(props.id);
  if (!taskDiv) return;

  const dragHandle = taskDiv.querySelector('.drag-handle');
  const content = taskDiv.querySelector('.content');
  const resizeHandles = taskDiv.querySelectorAll('.resize-handle');

  const observer = new MutationObserver(() => {
    let display = taskDiv.classList.contains("editable") ? "block" : "none";
    let notdisplay = taskDiv.classList.contains("editable") ? "none" : "block";

    if (!dragHandle) return;
    dragHandle.style.display = display;
    if (content) content.style.display = notdisplay;
    resizeHandles.forEach(handle => handle.style.display = display);
  });

  observer.observe(taskDiv, { attributes: true, attributeFilter: ["class"] });
});
</script>

<style scoped>
.task {
  overflow: hidden;
  position: absolute;
  border-radius: 4px;
}

.task.booking-pill {
  border-radius: 9999px;
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  z-index: 2;
}


.task.isopen {
  top: 0;
  height: 20px !important;
}

.task:active {
  opacity: 0.8;
  cursor: grabbing;
}

/* Abgeleitete Buchung (Entwurf Bjoern, TIM-74): Anker, Herkunft, Linie. Der
   Anker links verbindet den Namen der Quelle mit der Linie und markiert den
   Beginn; die Linie laeuft bis zum Ende der Buchung. */
.task.derived,
.task.derived:active {
  cursor: default;
}

.derived-anchor {
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 3px;
  width: 3px;
  border-radius: 1.5px;
}

.derived-body {
  position: absolute;
  left: 7px;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  overflow: hidden;
}

.derived-source {
  font-size: 9px;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.derived-main {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.derived-name {
  font-size: 11px;
  line-height: 1.15;
  color: #1f2937;
  white-space: nowrap;
  flex: none;
}

.derived-line {
  /* Kraeftiger als ein Haarstrich: bei 1 px verschwand die Linie neben den
     gefuellten Pillen und las sich eher wie ein Trennstrich als wie eine
     Buchung (Rueckmeldung Bjoern). */
  height: 2px;
  border-radius: 1px;
  flex: 1 1 auto;
  min-width: 0;
}

/* ✔ Day cells */
.day-container {
  display: flex;
  height: 100%;
  width: 100%;
  pointer-events: none; /* Prevent blocking drag/resize */
}

.day-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: gray;
  user-select: none;
}

.day-cell.available {
  color: rgb(240, 255, 240);
}

.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 30px;
  margin: 0 -10px;
  z-index: 2;
  background: transparent;
  cursor: ew-resize;
  display: none;
  touch-action: none;
}

.resize-handle::before {
  content: "";
  position: absolute;
  top: 25%;
  bottom: 25%;
  width: 10px;
  background-color: #666;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
}

.resize-handle.left { left: 5px; }
.resize-handle.right { right: 5px; }

.drag-handle {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background-color: #444;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  cursor: grab;
  z-index: 10;
  touch-action: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.printview {
  touch-action: none !important;
  pointer-events: none !important;
  user-select: none !important;
}


.day-container-pdf {
  display: inline-block;
  white-space: nowrap; /* horizontal layout */
  height: 30px;
}

.day-cell-pdf {
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  height: 100%;
}


</style>
